import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { PlayCircle, ArrowLeft, Music2 } from "lucide-react";
import api from "../lib/api";
import { useRadioPlayer } from "../context/RadioPlayerContext";

/**
 * RadioPlaylist — curated or Top-N playlist view. Identical structure to
 * `RadioStation` but sourced from /api/radio/playlists/{slug}.
 */
export default function RadioPlaylist() {
  const { slug } = useParams();
  const { playQueue, current } = useRadioPlayer();
  const [pl, setPl] = useState(null);

  useEffect(() => {
    api.get(`/radio/playlists/${slug}`).then((r) => setPl(r.data)).catch(() => setPl({ error: true }));
  }, [slug]);

  if (!pl) return <div className="min-h-screen bg-[#05060a] flex items-center justify-center text-white/40">Loading…</div>;
  if (pl.error) return <div className="p-10 text-center text-white/60">Playlist not found.</div>;
  const tracks = pl.tracks || [];

  return (
    <div data-testid="radio-playlist-page" className="min-h-screen bg-[#05060a] text-white pb-[160px]">
      <section
        className="relative"
        style={{
          minHeight: 320,
          backgroundImage: `linear-gradient(to bottom, rgba(5,6,10,0.5), rgba(5,6,10,1)), url(${pl.cover_url || ""})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-10">
          <Link to="/radio" className="inline-flex items-center gap-1 text-white/70 hover:text-white text-sm mb-4">
            <ArrowLeft className="w-4 h-4" /> Radio
          </Link>
          <div className="text-tv-accent uppercase tracking-[0.3em] text-[11px] font-black mb-2">Playlist</div>
          <h1 className="font-display text-5xl sm:text-6xl font-black mb-2">{pl.name}</h1>
          <p className="text-white/75 text-base max-w-[640px]">{pl.description}</p>
          <button
            data-testid="radio-playlist-play"
            onClick={() => playQueue(tracks, 0, { source: `playlist:${pl.slug}` })}
            disabled={!tracks.length}
            className="mt-5 inline-flex items-center gap-2 bg-tv-accent disabled:opacity-40 hover:bg-yellow-400 text-black font-display font-black text-lg px-6 py-3 rounded-full"
          >
            <PlayCircle className="w-6 h-6" /> Play Playlist
          </button>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-6 divide-y divide-white/5" data-testid="radio-playlist-tracks">
        {tracks.length === 0 && (
          <div className="text-white/50 py-10 text-center">
            <Music2 className="w-8 h-8 mx-auto mb-2 text-white/30" />
            Empty playlist.
          </div>
        )}
        {tracks.map((t, i) => {
          const isCurrent = current?.id === t.id;
          return (
            <button
              key={t.id}
              data-testid={`radio-playlist-track-${t.id}`}
              onClick={() => playQueue(tracks, i, { source: `playlist:${pl.slug}` })}
              className="w-full flex items-center gap-3 py-2.5 hover:bg-white/5 rounded-lg px-2 text-left transition"
            >
              <div className={`w-7 text-center tabular-nums text-sm font-black ${isCurrent ? "text-tv-accent" : "text-white/40"}`}>{i + 1}</div>
              <div
                className="shrink-0 w-12 h-12 rounded bg-white/10 overflow-hidden"
                style={{ backgroundImage: `url(${t.cover_url || pl.cover_url || ""})`, backgroundSize: "cover", backgroundPosition: "center" }}
              />
              <div className="flex-1 min-w-0">
                <div className={`font-bold text-sm line-clamp-1 ${isCurrent ? "text-tv-accent" : ""}`}>{t.title}</div>
                <div className="text-white/55 text-xs line-clamp-1">{t.artist}{t.album ? ` · ${t.album}` : ""}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
